import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { Wilayah } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: { is_papua: boolean; is_3t: boolean }) => {
  const response = await api.post("/master/wilayah/store", newData)
  return response.data
}

const patchData = async (newData: {
  id_ref_wilayah: string
  is_papua: boolean
  is_3t: boolean
}) => {
  const response = await api.patch("/master/wilayah/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_wilayah: string }) => {
  const response = await api({
    url: "/master/wilayah/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Wilayah | null
  closeModal: () => void
}) {
  const [form, setForm] = useState({
    id_ref_wilayah: selectedRow?.id_ref_wilayah || "",
    is_papua: selectedRow?.is_papua ?? false,
    is_3t: selectedRow?.is_3t ?? false,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listWilayah"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jalur"
          description={`Apakah anda yakin akan menghapus data jalur "${selectedRow?.nama_wilayah}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Wilayah" : "Update Data Wilayah"}
      />
      <div className="flex gap-3">
        <Switch
          id="is_papua"
          checked={form.is_papua}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_papua: !prev.is_papua,
            }))
          }
        />
        <Label htmlFor="status">Wilayah Papua (tidak/ya)</Label>
      </div>
      <div className="flex gap-3">
        <Switch
          id="is_3t"
          checked={form.is_3t}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_3t: !prev.is_3t,
            }))
          }
        />
        <Label htmlFor="status">Wilayah 3T (tidak/ya)</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
